import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { BarChart3, BookOpen, Calendar, Megaphone, Plus, ScanLine } from "lucide-react";
import { applyPosterFallback, getEventId, getEventPoster } from "@/lib/eventDisplay";

const statusTone = {
  approved: "default",
  pending: "outline",
  resubmitted: "outline",
  needs_changes: "secondary",
  rejected: "destructive",
};

export default function MyEvents() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!user) return;
    setBusy(true);
    api.get("/events/my")
      .then((r) => setEvents(r.data || []))
      .catch(() => setEvents([]))
      .finally(() => setBusy(false));
  }, [user]);

  if (loading) return <div className="min-h-screen bg-background"><Navbar /></div>;
  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="mx-auto max-w-md px-5 py-16 text-center">
          <h1 className="font-display text-2xl font-black">Sign in to view your events</h1>
          <Button onClick={() => navigate("/login", { state: { from: "/my-events" } })} className="mt-4 rounded-full">Sign in</Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-6xl px-5 md:px-8 py-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">Organise</p>
            <h1 className="font-display text-3xl md:text-4xl font-black flex items-center gap-3">
              <Calendar className="h-7 w-7 text-primary" /> My Events
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">Manage dashboards, ticket sales, scanner setup and featured boosts.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate("/organiser-guide")} className="rounded-full">
              <BookOpen className="mr-2 h-4 w-4" /> Organiser guide
            </Button>
            <Button onClick={() => navigate("/create-event")} className="rounded-full">
              <Plus className="mr-2 h-4 w-4" /> New event
            </Button>
          </div>
        </div>

        {busy ? (
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-44 rounded-2xl border border-border bg-muted/40" />)}
          </div>
        ) : events.length === 0 ? (
          <section className="mt-10 rounded-2xl border border-dashed border-border p-10 text-center">
            <p className="font-display text-2xl font-black">No events yet</p>
            <p className="mt-2 text-sm text-muted-foreground">Create your first event and submit it for admin approval.</p>
            <Button onClick={() => navigate("/create-event")} className="mt-4 rounded-full">Create event</Button>
          </section>
        ) : (
          <section className="mt-8 grid gap-4 md:grid-cols-2">
            {events.map((event) => {
              const eventId = getEventId(event);
              const poster = getEventPoster(event);
              const isRejected = event.status === "rejected";
              const primaryHref = eventId ? (isRejected ? `/event/${eventId}` : `/organiser/${eventId}`) : "/my-events";
              const reviewMessage = event.review_note || event.rejection_reason;
              return (
                <article key={eventId || event.title} className="overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-0.5 hover:border-primary/50">
                  <a href={primaryHref} className="block w-full text-left">
                    <div className="flex">
                      <img src={poster} alt="" loading="lazy" decoding="async" className="h-36 w-28 object-cover bg-muted sm:w-36" onError={(e) => applyPosterFallback(e, event)} />
                      <div className="min-w-0 flex-1 p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant={statusTone[event.status] || "outline"}>{event.status || "draft"}</Badge>
                          <span className="text-xs font-bold text-primary">{event.category}</span>
                        </div>
                        <h2 className="mt-2 font-display text-xl font-black line-clamp-2">{event.title}</h2>
                        <p className="mt-1 text-xs text-muted-foreground">{event.date} · {event.city}</p>
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-1">{event.venue}</p>
                        {reviewMessage && (
                          <p className={`mt-2 rounded-lg p-2 text-xs line-clamp-2 ${isRejected ? "bg-rose-500/10 text-rose-700" : "bg-amber-500/10 text-amber-700"}`}>
                            {isRejected ? "Rejection reason" : "Admin note"}: {reviewMessage}
                          </p>
                        )}
                      </div>
                    </div>
                  </a>
                  <div className={`${event.status === "needs_changes" ? "grid-cols-4" : isRejected ? "grid-cols-1" : "grid-cols-3"} grid border-t border-border text-xs`}>
                    {isRejected ? (
                      <a href={eventId ? `/event/${eventId}` : "/my-events"} className={`flex items-center justify-center gap-1.5 px-2 py-3 font-bold hover:bg-muted ${!eventId ? "pointer-events-none opacity-50" : ""}`}>
                        <Calendar className="h-4 w-4 text-primary" /> View event page
                      </a>
                    ) : (
                      <a href={eventId ? `/organiser/${eventId}` : "/my-events"} className={`flex items-center justify-center gap-1.5 px-2 py-3 font-bold hover:bg-muted ${!eventId ? "pointer-events-none opacity-50" : ""}`}>
                        <BarChart3 className="h-4 w-4 text-primary" /> Dashboard
                      </a>
                    )}
                    {event.status === "needs_changes" && (
                      <button onClick={() => navigate(`/create-event?edit=${eventId}`)} className="flex items-center justify-center gap-1.5 px-2 py-3 font-bold hover:bg-muted">
                        <Plus className="h-4 w-4 text-primary" /> Edit
                      </button>
                    )}
                    {!isRejected && (
                      <>
                        <a href={eventId ? `/scanner?event=${eventId}` : "/my-events"} className={`flex items-center justify-center gap-1.5 px-2 py-3 font-bold hover:bg-muted ${!eventId ? "pointer-events-none opacity-50" : ""}`}>
                          <ScanLine className="h-4 w-4 text-primary" /> Scanner
                        </a>
                        <a href={eventId ? `/organiser/${eventId}?tab=ads` : "/my-events"} className={`flex items-center justify-center gap-1.5 px-2 py-3 font-bold hover:bg-muted ${!eventId ? "pointer-events-none opacity-50" : ""}`}>
                          <Megaphone className="h-4 w-4 text-primary" /> Boost
                        </a>
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </section>
        )}
      </main>
    </div>
  );
}
